/**
 * Error handling and categorization utilities
 * @module utils/error-handler
 */

/**
 * Error category types
 */
export type ErrorCategory =
  | 'network'
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'not-found'
  | 'conflict'
  | 'server'
  | 'unknown'

/**
 * Categorized error information
 */
export interface CategorizedError {
  category: ErrorCategory
  message: string
  userMessage: string
  userMessagePtBR: string
  statusCode?: number
  isRetriable: boolean
  originalError: unknown
}

/**
 * Error messages in English and Portuguese
 */
interface ErrorMessages {
  en: string
  ptBR: string
}

/**
 * User-friendly error messages by category
 */
const errorMessages: Record<ErrorCategory, ErrorMessages> = {
  network: {
    en: 'Unable to connect to the server. Please check your internet connection and try again.',
    ptBR:
      'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.'
  },
  authentication: {
    en: 'Your session has expired. Please sign in again.',
    ptBR: 'Sua sessão expirou. Por favor, faça login novamente.'
  },
  authorization: {
    en: 'You do not have permission to perform this action.',
    ptBR: 'Você não tem permissão para realizar esta ação.'
  },
  validation: {
    en: 'The information provided is invalid. Please check your input and try again.',
    ptBR:
      'As informações fornecidas são inválidas. Por favor, verifique os dados e tente novamente.'
  },
  'not-found': {
    en: 'The requested resource was not found.',
    ptBR: 'O recurso solicitado não foi encontrado.'
  },
  conflict: {
    en: 'This resource has been modified by another user. Please refresh and try again.',
    ptBR:
      'Este recurso foi modificado por outro usuário. Por favor, atualize e tente novamente.'
  },
  server: {
    en: 'The server is temporarily unavailable. Please try again in a few moments.',
    ptBR:
      'O servidor está temporariamente indisponível. Por favor, tente novamente em alguns instantes.'
  },
  unknown: {
    en: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
    ptBR:
      'Ocorreu um erro inesperado. Por favor, tente novamente ou entre em contato com o suporte se o problema persistir.'
  }
}

/**
 * Extract HTTP status code from an error
 */
const getStatusCode = (error: unknown): number | undefined => {
  // Check for response.status
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  // Check for statusCode property
  if (
    error &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
  ) {
    return error.statusCode
  }

  // Check for $metadata.httpStatusCode (AWS SDK v3)
  if (
    error &&
    typeof error === 'object' &&
    '$metadata' in error &&
    error.$metadata &&
    typeof error.$metadata === 'object' &&
    'httpStatusCode' in error.$metadata &&
    typeof error.$metadata.httpStatusCode === 'number'
  ) {
    return error.$metadata.httpStatusCode
  }

  return undefined
}

/**
 * Extract error message from an error object
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }

  return 'Unknown error'
}

/**
 * Categorize an error based on its type and properties
 *
 * @param error - The error to categorize
 * @returns Categorized error information with user-friendly messages
 *
 * @example
 * ```typescript
 * try {
 *   await fetchEmployees()
 * } catch (error) {
 *   const categorized = categorizeError(error)
 *   console.log(categorized.userMessage)
 *   console.log(categorized.userMessagePtBR)
 * }
 * ```
 */
export const categorizeError = (error: unknown): CategorizedError => {
  const statusCode = getStatusCode(error)
  const message = getErrorMessage(error)

  // Network errors (no response from server)
  if (
    error instanceof TypeError &&
    (error.message.includes('fetch') || error.message.includes('network'))
  ) {
    return {
      category: 'network',
      message,
      userMessage: errorMessages.network.en,
      userMessagePtBR: errorMessages.network.ptBR,
      isRetriable: true,
      originalError: error
    }
  }

  // Amplify network errors
  if (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    const errorName = error.name
    if (
      errorName === 'NetworkError' ||
      errorName === 'TimeoutError' ||
      errorName === 'AbortError'
    ) {
      return {
        category: 'network',
        message,
        userMessage: errorMessages.network.en,
        userMessagePtBR: errorMessages.network.ptBR,
        isRetriable: true,
        originalError: error
      }
    }
  }

  // HTTP status code based categorization
  if (statusCode) {
    // Authentication errors
    if (statusCode === 401) {
      return {
        category: 'authentication',
        message,
        userMessage: errorMessages.authentication.en,
        userMessagePtBR: errorMessages.authentication.ptBR,
        statusCode,
        isRetriable: false,
        originalError: error
      }
    }

    // Authorization errors
    if (statusCode === 403) {
      return {
        category: 'authorization',
        message,
        userMessage: errorMessages.authorization.en,
        userMessagePtBR: errorMessages.authorization.ptBR,
        statusCode,
        isRetriable: false,
        originalError: error
      }
    }

    // Validation errors
    if (statusCode === 400 || statusCode === 422) {
      return {
        category: 'validation',
        message,
        userMessage: errorMessages.validation.en,
        userMessagePtBR: errorMessages.validation.ptBR,
        statusCode,
        isRetriable: false,
        originalError: error
      }
    }

    // Not found errors
    if (statusCode === 404) {
      return {
        category: 'not-found',
        message,
        userMessage: errorMessages['not-found'].en,
        userMessagePtBR: errorMessages['not-found'].ptBR,
        statusCode,
        isRetriable: false,
        originalError: error
      }
    }

    // Conflict errors
    if (statusCode === 409) {
      return {
        category: 'conflict',
        message,
        userMessage: errorMessages.conflict.en,
        userMessagePtBR: errorMessages.conflict.ptBR,
        statusCode,
        isRetriable: false,
        originalError: error
      }
    }

    // Server errors (5xx)
    if (statusCode >= 500) {
      return {
        category: 'server',
        message,
        userMessage: errorMessages.server.en,
        userMessagePtBR: errorMessages.server.ptBR,
        statusCode,
        isRetriable: true,
        originalError: error
      }
    }
  }

  // Default to unknown error
  return {
    category: 'unknown',
    message,
    userMessage: errorMessages.unknown.en,
    userMessagePtBR: errorMessages.unknown.ptBR,
    statusCode,
    isRetriable: false,
    originalError: error
  }
}

/**
 * Get user-friendly error message in English
 */
export const getUserFriendlyMessage = (
  error: unknown,
  locale: 'en' | 'pt-BR' = 'pt-BR'
): string => {
  const categorized = categorizeError(error)
  return locale === 'pt-BR'
    ? categorized.userMessagePtBR
    : categorized.userMessage
}

/**
 * Check if an error is retriable
 */
export const isRetriableError = (error: unknown): boolean => {
  const categorized = categorizeError(error)
  return categorized.isRetriable
}

/**
 * Format error for logging (sanitized, structured)
 */
export const formatErrorForLogging = (
  error: unknown
): {
  category: ErrorCategory
  message: string
  statusCode?: number
  stack?: string
} => {
  const categorized = categorizeError(error)

  return {
    category: categorized.category,
    message: categorized.message,
    statusCode: categorized.statusCode,
    stack: error instanceof Error ? error.stack : undefined
  }
}
